import * as ClientActions from './ClientActions.js';

const EMPTY_CLIENT_INFORMATION = {
  children: [{}]
};

const initialState = {
  isNewClient: true,
  hasChildren: true,
  hasServicesForClient: false,
  childrenSelectedForServices: [],
  isClientNotFound: false,
  clientVariations: [],
  clientEmployee: [],
  clientInformation: {
    ...EMPTY_CLIENT_INFORMATION,
    children: [{}]
  },
  clientSaveError: null,
  newChild: {}
};

export default function client(state = initialState, action) {
  switch (action.type) {
    case 'UNAUTHORIZED':
      return {
        ...initialState
      };
    case 'LOGOUT':
      return {
        ...initialState
      };
    case ClientActions.CLEAR_CLIENT:
      return {
        ...state,
        clientInformation: { ...EMPTY_CLIENT_INFORMATION, children: [{}] },
        clientVariations: [],
        clientEmployee: [],
        childrenSelectedForServices: [],
        isNewClient: true,
        isClientNotFound: false,
        clientSaveError: null
      };
    case ClientActions.SET_NEW_CLIENT:
      return {
        ...state,
        isNewClient: action.data,
        isClientNotFound: false,
        clientSaveError: null
      };
    case ClientActions.SET_CLIENT_NOT_FOUND:
      return {
        ...state,
        isClientNotFound: action.data
      };
    case ClientActions.SET_HAS_SERVICES_FOR_CLIENT:
      return {
        ...state,
        hasServicesForClient: action.data
      };
    case ClientActions.SET_HAS_CHILDREN:
      return {
        ...state,
        hasChildren: action.data.hasChildren,
        hasServicesForClient: action.data.hasServicesForClient
      };
    case ClientActions.SET_CLIENT_INFORMATION:
      return {
        ...state,
        clientInformation: {
          ...action.data,
          children:
            action.data.children && action.data.children.length > 0
              ? action.data.children
              : [{}]
        },
        clientSaveError: null
      };
    case ClientActions.SET_CLIENT_CHILD_INFORMATION:
      const children = state.clientInformation.children;
      const { childInformation, childIndex } = action.data;

      if (childIndex != null) {
        children[childIndex] = { ...childInformation };
        return {
          ...state,
          clientInformation: { ...state.clientInformation, children: children },
          clientSaveError: null
        };
      } else {
        return {
          ...state,
          newChild: childInformation,
          clientSaveError: null
        };
      }
    case ClientActions.CLEAR_NEW_CHILD:
      return {
        ...state,
        newChild: {},
        clientSaveError: null
      };
    case ClientActions.SET_CLIENT_SAVE_ERROR:
      return {
        ...state,
        clientSaveError: action.data
      };
    case ClientActions.SET_CLIENT_VARIATION:
      const clientId = action.data.clientId;
      const childId = action.data.childId;
      const variationId = action.data.variationId;
      const parentVariationId = action.data.parentVariationId;

      let newClientVariations = state.clientVariations.filter(
        cv =>
          !(
            cv.clientId === clientId &&
            cv.childId === childId &&
            cv.variationId === variationId &&
            cv.parentVariationId === parentVariationId
          )
      );

      if (!action.data.selected) {
        newClientVariations = newClientVariations.filter(
          cv =>
            !(
              cv.clientId === clientId &&
              cv.childId === childId &&
              cv.parentVariationId === variationId
            )
        );
      }

      if (action.data.selected) {
        newClientVariations.push({
          clientId,
          childId,
          variationId,
          parentVariationId
        });
      }

      return {
        ...state,
        clientVariations: newClientVariations
      };

    case ClientActions.CLEAR_CLIENT_EMPLOYEE:
      state.clientEmployee.forEach((ce) => {
        ce.employeeId = 0
      });
      return state
      
    case ClientActions.SET_CLIENT_EMPLOYEE:
      const newClientEmployee = state.clientEmployee.filter(
        ce =>
          !(
            ce.clientId === action.data.clientId &&
            ce.childId === action.data.childId
          )
      );
      newClientEmployee.push({
        clientId: action.data.clientId,
        childId: action.data.childId,
        employeeId: action.data.employeeId,
        estimation: action.data.estimation
      });

      return {
        ...state,
        clientEmployee: newClientEmployee
      };

    case ClientActions.UPDATE_CLIENT_EMPLOYEE_ESTIMATIONS:
      const estimations = action.data.estimations;
      const estimationsHash = {};
      const clientEmployees = state.clientEmployee;

      // Get all next available time estimations
      const nextStaffEstimations = [];
      estimations.forEach((estimation) => {
        if (estimation.employee_id === null) {
          nextStaffEstimations.push(estimation);
        }
      });
      nextStaffEstimations.forEach((nextStaffEstimation) => {
        estimationsHash[
          `${nextStaffEstimation.client_id}_${nextStaffEstimation.child_id}_0`
        ] = nextStaffEstimation;
      });

      estimations.forEach((estimation) => {
        estimationsHash[
          `${estimation.client_id}_${estimation.child_id}_${estimation.employee_id}`
        ] = estimation;
      });

      clientEmployees.forEach((employee) => {
        const newEstimation =
          estimationsHash[
            `${employee.clientId}_${employee.childId}_${employee.employeeId}`
          ];
        /**
         * In case we selected "next available", there are situations where
         * instead of preserving "0" as an employee_id, on the estimations objects
         * received from the backend, we would sometimes get the ACTUAL employee ID, thus
         * losing information which would let us know that "next available" is still selected.
         *
         * In this case, we are going to also look for updating estimations for "next available",
         * but base that on the REAL employee id value.
         *
         * specificEmployeeBackupEstimation - this is a backup, in case newEstimation is undefined
         */
        const specificEmployeeBackupEstimation = estimationsHash[
          `${employee.clientId}_${employee.childId}_${employee.estimation?.real_employee_id}`
        ];
        const selectedEstimation = newEstimation || specificEmployeeBackupEstimation
        if (selectedEstimation) {
          employee.estimation = {
            ...employee.estimation,
            remaining: selectedEstimation.remaining,
            begin_at: selectedEstimation.chosen_time?.begin_at,
            end_at: selectedEstimation.chosen_time?.end_at,
            real_employee_id: selectedEstimation.chosen_time.employee_id,
          };
        }
      });

      return {
        ...state,
        clientEmployee: clientEmployees,
      };
      
    case ClientActions.SET_CHILD_SELECTED_FOR_SERVICE:
      let selectedChildren = [...state.childrenSelectedForServices];

      if (
        action.data.selected &&
        selectedChildren.indexOf(action.data.childId) === -1
      ) {
        selectedChildren.push(action.data.childId);
      } else if (!action.data.selected) {
        selectedChildren = selectedChildren.filter(
          c => c !== action.data.childId
        );
      }

      return {
        ...state,
        childrenSelectedForServices: selectedChildren
      };

    default:
      return state;
  }
}
